import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, extractErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/BrandLogo";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, Building2 } from "lucide-react";

const fmt = (cur, n) => new Intl.NumberFormat("en-US", { style: "currency", currency: cur || "USD" }).format(Number(n || 0));

export default function AgencyPay() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/agency/pay/${token}`)
      .then(({ data }) => setInfo(data))
      .catch((e) => setErr(extractErrorMessage(e, "This payment link is no longer valid.")));
  }, [token]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/agency/pay/${token}/stripe/checkout`, { origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not start checkout."));
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-5 py-12">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="agency-pay-card">
        <div className="mb-6 flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 p-1"><BrandLogo className="h-full w-auto" /></div>
          <p className="font-display text-sm font-extrabold text-slate-900">AR Process Service</p>
        </div>
        {err && <p className="text-sm text-red-600" data-testid="agency-pay-error">{err}</p>}
        {!err && !info && <p className="text-sm text-slate-500">Loading…</p>}
        {info && info.paid && (
          <div className="text-center" data-testid="agency-pay-already-paid">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
            <h1 className="mt-4 font-display text-xl font-bold text-slate-900">Payment complete</h1>
            <p className="mt-2 text-sm text-slate-600">Your first payment for {info.agency_name} has been received. Watch your inbox for your activation invitation.</p>
          </div>
        )}
        {info && !info.paid && (
          <div>
            <div className="mb-4 flex items-center gap-2 text-slate-700"><Building2 className="h-4 w-4" /><span className="text-sm font-semibold">{info.agency_name}</span></div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">First payment</h1>
            <p className="mt-2 text-sm text-slate-600">Complete your first payment to begin your ARPS platform relationship. Your activation invitation is sent only after this payment is confirmed.</p>
            <div className="my-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-center">
              <p className="text-xs font-medium uppercase tracking-widest text-slate-500">Amount due</p>
              <p className="mt-1 text-3xl font-extrabold text-slate-900" data-testid="agency-pay-amount">{fmt(info.currency, info.amount)}</p>
            </div>
            <Button data-testid="agency-pay-button" onClick={pay} disabled={busy} className="w-full bg-sky-600 text-white hover:bg-sky-700">
              <CreditCard className="mr-2 h-4 w-4" />{busy ? "Redirecting…" : "Pay with card"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
